import React, { useMemo } from 'react'
import WSManager from '../../connection/websocket'
import SportManager from '../../managers/sport'
import { DateUtils, getUID, logger } from '../../utils'
import { useApplicationState } from '../root'
import { useSettings } from '../settings'
import { SPORT_SERVICE, STATUS } from '../../constants'
import Bus from '../../bus'
import { isArray, isEmpty } from 'lodash'
import {
  CATEGORY_EVENT_COUNT,
  SPORT_EVENT_COUNT,
  TOURNAMENT_EVENT_COUNT
} from '../../connection/subscription/subscription.topics'

export const SportsActions = {
  ADD_LOADER: 'ADD_LOADER',
  REMOVE_LOADER: 'REMOVE_LOADER',
  SPORT_RESET_STATE: 'SPORT_RESET_STATE',
  SPORT_OPTIMISE_STATE: 'SPORT_OPTIMISE_STATE',
  SPORT_EVENT_UPDATE: 'SPORT_EVENT_UPDATE',
  SPORT_EVENTS_UPDATE: 'SPORT_EVENTS_UPDATE',
  SPORT_EVENTS_UPDATE_MARKET_TYPE: 'SPORT_EVENTS_UPDATE_MARKET_TYPE',
  SPORT_LIST_ITEM_UPDATE: 'SPORT_LIST_ITEM_UPDATE',
  SPORT_LIST_UPDATE: 'SPORT_LIST_UPDATE',
  SPORT_STATE_UPDATE: 'SPORT_STATE_UPDATE',
  SPORT_CATEGORY_UPDATE: 'SPORT_CATEGORY_UPDATE',
  SPORT_CATEGORIES_UPDATE: 'SPORT_CATEGORIES_UPDATE',
  SPORT_TOURNAMENT_UPDATE: 'SPORT_TOURNAMENT_UPDATE',
  SPORT_TOURNAMENTS_UPDATE: 'SPORT_TOURNAMENTS_UPDATE',
  RESET_ERROR: 'RESET_ERROR'
}

const addSportToState = (state, data) => {
  if (!data || !data.id) {
    return state
  }

  // websocket may send data for a something that hasn't been loaded
  // todo: perhaps we need to lazy-load the full thing?
  const existing = state.sportsById[data.id]
  if (!existing && !data.name) {
    return state
  }

  const newSport = existing
    ? {
      ...existing,
      ...data
    }
    : data

  return {
    ...state,
    sportsById: {
      ...state.sportsById,
      [newSport.id]: newSport
    }
  }
}

const addCategoryToState = (state, data) => {
  if (!data || !data.id) {
    return state
  }

  // websocket may send data for a something that hasn't been loaded
  const existing = state.categoriesById[data.id]
  if (!existing && !data.name) {
    return state
  }

  const newCategory = existing
    ? {
      ...existing,
      ...data
    }
    : data

  return {
    ...state,
    categoriesById: {
      ...state.categoriesById,
      [newCategory.id]: newCategory
    },
    categoriesBySportId: {
      ...state.categoriesBySportId,
      [newCategory.sportId]: [
        ...(state.categoriesBySportId[newCategory.sportId] || []).filter(e => e.id !== newCategory.id),
        newCategory
      ]
    }
  }
}

const addTournamentToState = (state, data) => {
  if (!data || !data.id) {
    return state
  }

  // websocket may send data for a something that hasn't been loaded
  const existing = state.tournamentsById[data.id]
  if (!existing && !data.name) {
    return state
  }

  const newTournament = existing
    ? {
      ...existing,
      ...data
    }
    : data
  return {
    ...state,
    tournamentsById: {
      ...state.tournamentsById,
      [newTournament.id]: newTournament
    },
    tournamentsBySportId: {
      ...state.tournamentsBySportId,
      [newTournament.sportId]: [
        ...(state.tournamentsBySportId[newTournament.sportId] || []).filter(e => e.id !== newTournament.id),
        newTournament
      ]
    },
    tournamentsByCategoryId: {
      ...state.tournamentsByCategoryId,
      [newTournament.categoryId]: [
        ...(state.tournamentsByCategoryId[newTournament.categoryId] || []).filter(e => e.id !== newTournament.id),
        newTournament
      ]
    }
  }
}

const addEventToState = (state, data) => {
  if (!data || !data.id) {
    return state
  }

  let newEvent = data
  if (state.eventsById[data.id]) {
    let marketTypes = state.eventsById[data.id].marketTypes || {}
    for (const mt in marketTypes) {
      if (!data.marketTypes?.[mt]) {
        continue
      }

      let markets = marketTypes[mt].markets || {}
      for (const m in markets) {
        if (!data.marketTypes[mt].markets?.[m]) {
          continue
        }

        let outcomes = markets[m].outcomes || {}
        for (const o in outcomes) {
          if (!data.marketTypes[mt].markets[m].outcomes?.[o]) {
            continue
          }
          outcomes[o] = {
            ...outcomes[o],
            ...data.marketTypes[mt].markets[m].outcomes[o]
          }
        }

        // Merge new outcomes
        const newOutcomes = data.marketTypes[mt].markets[m].outcomes || {}
        for (const o in newOutcomes) {
          if (outcomes[o]) {
            continue
          }
          outcomes = {
            ...outcomes,
            [o]: newOutcomes[o]
          }
        }

        markets[m] = {
          ...markets[m],
          ...data.marketTypes[mt].markets[m],
          outcomes: outcomes
        }
      }

      // Merge new markets
      const newMarkets = data.marketTypes[mt].markets || {}
      for (const m in newMarkets) {
        if (markets[m] || isEmpty(newMarkets[m].outcomes)) {
          continue
        }
        markets = {
          ...markets,
          [m]: newMarkets[m]
        }
      }

      marketTypes[mt] = {
        ...marketTypes[mt],
        ...data.marketTypes[mt],
        markets: markets
      }
    }

    // Merge new market types
    const newMarketTypes = data.marketTypes || {}
    for (const mt in newMarketTypes) {
      if (marketTypes[mt] || isEmpty(newMarketTypes[mt].markets)) {
        continue
      }
      marketTypes = {
        ...marketTypes,
        [mt]: newMarketTypes[mt]
      }
    }

    newEvent = {
      ...state.eventsById[data.id],
      ...data,
      marketTypes: marketTypes
    }
  }

  return {
    ...state,
    eventsById: {
      ...state.eventsById,
      [newEvent.id]: newEvent
    },
    eventsBySportId: {
      ...state.eventsBySportId,
      [newEvent.sportId]: [
        ...(state.eventsBySportId[newEvent.sportId] || []).filter(e => e.id !== newEvent.id),
        newEvent
      ]
    },
    eventsByCategoryId: {
      ...state.eventsByCategoryId,
      [newEvent.categoryId]: [
        ...(state.eventsByCategoryId[newEvent.categoryId] || []).filter(e => e.id !== newEvent.id),
        newEvent
      ]
    },
    eventsByTournamentId: {
      ...state.eventsByTournamentId,
      [newEvent.tournamentId]: [
        ...(state.eventsByTournamentId[newEvent.tournamentId] || []).filter(e => e.id !== newEvent.id),
        newEvent
      ]
    }
  }
}

const removeEventFromState = (state, data) => {
  if (!data || !data.id) {
    return state
  }

  const eventsById = {
    ...state.eventsById,
    [data.id]: null
  }
  delete eventsById[data.id]

  return {
    ...state,
    eventsById,
    eventsBySportId: {
      ...state.eventsBySportId,
      [data.sportId]: (state.eventsBySportId[data.sportId] || []).filter(e => e.id !== data.id)
    },
    eventsByCategoryId: {
      ...state.eventsByCategoryId,
      [data.categoryId]: (state.eventsByCategoryId[data.categoryId] || []).filter(e => e.id !== data.id)
    },
    eventsByTournamentId: {
      ...state.eventsByTournamentId,
      [data.tournamentId]: (state.eventsByTournamentId[data.tournamentId] || []).filter(e => e.id !== data.id)
    }
  }
}

const sportInitialState = {
  initialized: false,
  loading: false,
  activeLoaders: 0,
  error: null,

  sportsById: {},

  categoriesById: {},
  categoriesBySportId: {},

  tournamentsById: {},
  tournamentsByCategoryId: {},
  tournamentsBySportId: {},

  eventsById: {},
  eventsByTournamentId: {},
  eventsByCategoryId: {},
  eventsBySportId: {},

  selectedSportId: null,
  selectedCategoryId: null,
  selectedTournamentId: null,
  selectedEventId: null,

  selectedSportUrlName: null,
  selectedCategoryUrlName: null,
  selectedTournamentUrlName: null,
  selectedEventUrlName: null

  // These fields will be generated during state initialisation
  // id: null,
  // connected: null,
  // connectionId: null,
}

const createSportInitialState = options => {
  const {
    ...restOptions
  } = options

  const id = getUID(5)
  const connected = WSManager.connected
  const connectionId = WSManager.connectionId

  return {
    id,
    connected,
    connectionId,
    ...sportInitialState,
    ...restOptions
  }
}

const sportReducer = (state, action) => {
  logger.debug(`[sport:${state.id}] dispatching action`, action)

  switch (action.type) {
    case SportsActions.RESET_ERROR: {
      return {
        ...state,
        ...(action.payload || {}),
        error: null
      }
    }

    case SportsActions.ADD_LOADER: {
      const activeLoaders = state.activeLoaders += 1
      return {
        ...state,
        ...(action.payload || {}),
        activeLoaders,
        loading: true
      }
    }

    case SportsActions.REMOVE_LOADER: {
      let activeLoaders = state.activeLoaders -= 1
      if (activeLoaders < 0) {
        activeLoaders = 0
      }
      const loading = activeLoaders !== 0
      return {
        ...state,
        ...(action.payload || {}),
        activeLoaders,
        loading
      }
    }

    case SportsActions.SPORT_RESET_STATE: {
      return {
        ...state,
        initialized: false,
        loading: false,
        activeLoaders: 0,
        error: null,

        sportsById: {},

        categoriesById: {},
        categoriesBySportId: {},

        tournamentsById: {},
        tournamentsByCategoryId: {},
        tournamentsBySportId: {},

        eventsById: {},
        eventsByTournamentId: {},
        eventsByCategoryId: {},
        eventsBySportId: {}
      }
    }

    case SportsActions.SPORT_OPTIMISE_STATE: {
      const now = Date.now()
      return Object.values(state.eventsById).reduce((newState, event) => {
        if (!event || event.startTime < now) {
          return removeEventFromState(newState, event)
        }
        return newState
      }, state)
    }

    case SportsActions.SPORT_STATE_UPDATE:
      return {
        ...state,
        ...action.payload
      }

    case SportsActions.SPORT_LIST_UPDATE:
      return action.payload.reduce((newState, item) => {
        return addSportToState(newState, item)
      }, state)

    case SportsActions.SPORT_LIST_ITEM_UPDATE:
      return addSportToState(state, action.payload)

    case SportsActions.SPORT_CATEGORIES_UPDATE:
      return action.payload.reduce((newState, item) => {
        return addCategoryToState(newState, item)
      }, state)

    case SportsActions.SPORT_CATEGORY_UPDATE:
      return addCategoryToState(state, action.payload)

    case SportsActions.SPORT_TOURNAMENTS_UPDATE:
      return action.payload.reduce((newState, item) => {
        return addTournamentToState(newState, item)
      }, state)

    case SportsActions.SPORT_TOURNAMENT_UPDATE:
      return addTournamentToState(state, action.payload)

    case SportsActions.SPORT_EVENTS_UPDATE:
      return Object.values(action.payload)
        .reduce((newState, event) => {
          return addEventToState(newState, event)
        }, state)

    case SportsActions.SPORT_EVENTS_UPDATE_MARKET_TYPE: {
      const { eventId, marketTypes } = action.payload

      if (!eventId || !marketTypes) {
        return state
      }

      const existingEvent = state.eventsById[eventId]

      if (!existingEvent) {
        return state
      }

      const updatedMarketTypes = {
        ...existingEvent.marketTypes,
        ...marketTypes
      }

      const updatedEvent = {
        ...existingEvent,
        marketTypes: updatedMarketTypes
      }

      return {
        ...state,
        eventsById: {
          ...state.eventsById,
          [eventId]: updatedEvent
        }
      }
    }

    case SportsActions.SPORT_EVENT_UPDATE: {
      const event = action.payload
      // Socket event updates are partial models. REST are full models.
      const isFullModel = event?.status && event.sportService
      if (isFullModel && (event?.status?.toUpperCase() !== STATUS.ACTIVE)
      ) {
        return removeEventFromState(state, event)
      }
      return addEventToState(state, event)
    }

    default:
      return state
  }
}

export const createSportState = options => {
  const [state, dispatcher] = React.useReducer(
    sportReducer,
    (options || {}),
    createSportInitialState
  )

  const { connection } = useApplicationState()
  const { lang } = useSettings()

  const sportReInitHandler = payload => {
    dispatcher({
      type: SportsActions.SPORT_RESET_STATE
    })
  }

  const sportLoadCategoriesHandler = (payload) => {
    const fromDate = DateUtils.todayDateKey()
    const toDate = DateUtils.futureDateKey(30)

    const defaultFilters = {
      isTop: null,
      isOnlyMainMarketTypes: true,
      isOnlyActiveMarkets: true,
      sportServices: [
        // SPORT_SERVICE.LIVE,
        SPORT_SERVICE.PREMATCH
      ],
      sportId: null,
      categoryIds: null, // number[]
      fromDate,
      toDate
    }

    const defaultPaging = {
      itemsPerPage: 500,
      currentPage: 0
    }

    // Resolving sport categories
    dispatcher({
      type: SportsActions.ADD_LOADER
    })
    SportManager.findCategories({
      filter: {
        ...defaultFilters,
        ...(payload?.filter || {})
      },
      paging: {
        ...defaultPaging,
        ...(payload?.paging || {})
      }
    })
      .then(data => {
        dispatcher({
          type: SportsActions.SPORT_CATEGORIES_UPDATE,
          payload: data.data
        })
        dispatcher({
          type: SportsActions.REMOVE_LOADER,
          payload: {
            error: null
          }
        })
      })
      .catch(error => {
        logger.error('[sport state]', 'unable to load sport categories')
        dispatcher({
          type: SportsActions.REMOVE_LOADER,
          payload: {
            error
          }
        })
      })
  }

  const sportLoadTournamentsHandler = (payload) => {
    const fromDate = DateUtils.todayDateKey()
    const toDate = DateUtils.futureDateKey(30)

    const defaultFilters = {
      isTop: null,
      isOnlyMainMarketTypes: true,
      isOnlyActiveMarkets: true,
      sportServices: [
        // SPORT_SERVICE.LIVE,
        SPORT_SERVICE.PREMATCH
      ],
      sportId: null,
      categoryId: null,
      tournamentIds: null, // number[]
      fromDate,
      toDate
    }

    const defaultPaging = {
      itemsPerPage: 500,
      currentPage: 0
    }

    // Resolving sport tournaments
    dispatcher({
      type: SportsActions.ADD_LOADER
    })
    SportManager.findTournaments({
      filter: {
        ...defaultFilters,
        ...(payload?.filter || {})
      },
      paging: {
        ...defaultPaging,
        ...(payload?.paging || {})
      }
    })
      .then(data => {
        dispatcher({
          type: SportsActions.SPORT_TOURNAMENTS_UPDATE,
          payload: data.data
        })
        dispatcher({
          type: SportsActions.REMOVE_LOADER,
          payload: {
            error: null
          }
        })
      })
      .catch(error => {
        logger.error('[sport state]', 'unable to load sport tournaments')
        dispatcher({
          type: SportsActions.REMOVE_LOADER,
          payload: {
            error
          }
        })
      })
  }

  const eventUpdateHandler = (event) => {
    dispatcher({
      type: SportsActions.SPORT_EVENT_UPDATE,
      payload: event
    })
  }

  const sportCounterHandler = (event) => {
    dispatcher({
      type: SportsActions.SPORT_LIST_ITEM_UPDATE,
      payload: event?.data
    })
  }

  const categoryCounterHandler = (event) => {
    dispatcher({
      type: SportsActions.SPORT_CATEGORY_UPDATE,
      payload: event?.data
    })
  }

  const tournamentCounterHandler = (event) => {
    dispatcher({
      type: SportsActions.SPORT_TOURNAMENT_UPDATE,
      payload: event?.data
    })
  }

  // const socketEventUpdateHandler = (message) => {
  //  dispatcher({
  //    type: SportsActions.SPORT_SOCKET_EVENT_UPDATE,
  //   payload: message?.data?.events || {}
  //  })
  // }

  const sportLoadEventsHandler = (payload) => {
    const fromDate = Date.now()
    const toDate = fromDate + (12 * 3600000) // +12 hours

    const defaultFilters = {
      isTop: null,
      isOnlyMainMarketTypes: true,
      isOnlyActiveMarkets: false,
      sportServices: [
        // SPORT_SERVICE.LIVE,
        SPORT_SERVICE.PREMATCH
      ],
      sportId: null,
      tournamentIds: null,
      fromDate,
      toDate
    }

    const defaultPaging = {
      itemsPerPage: 250,
      currentPage: 0
    }

    // Resolving sport events
    dispatcher({
      type: SportsActions.ADD_LOADER
    })
    SportManager.findEvents({
      filter: {
        ...defaultFilters,
        ...(payload?.filter || {})
      },
      paging: {
        ...defaultPaging,
        ...(payload?.paging || {})
      }
    })
      .then(data => {
        dispatcher({
          type: SportsActions.SPORT_EVENTS_UPDATE,
          payload: data.events
        })
        dispatcher({
          type: SportsActions.REMOVE_LOADER,
          payload: {
            error: null
          }
        })
      })
      .catch(error => {
        logger.error('[sport state]', 'unable to load sport events')
        dispatcher({
          type: SportsActions.REMOVE_LOADER,
          payload: {
            error
          }
        })
      })
  }

  const sportLoadEventsMarketGroupHandler = (payload) => {
    dispatcher({
      type: SportsActions.ADD_LOADER
    })

    SportManager.findEvent(payload.eventId, { includeMarketGroups: payload.marketGroup })
      .then((event) => {
        dispatcher({
          type: SportsActions.SPORT_EVENTS_UPDATE_MARKET_TYPE,
          payload: {
            eventId: payload.eventId,
            marketTypes: event.marketTypes
          }
        })
        dispatcher({
          type: SportsActions.REMOVE_LOADER
        })
      })
      .catch((error) => {
        logger.error('[sport state]', 'unable to load sport events by market type')
        dispatcher({
          type: SportsActions.REMOVE_LOADER,
          payload: {
            error
          }
        })
      })
  }

  const sportLoadSingleEventHandler = (payload) => {
    dispatcher({
      type: SportsActions.ADD_LOADER
    })

    SportManager.findEvent(payload.eventId, {
      includeMarketGroups: payload.marketGroup,
      listAllMarketGroups: true
    })
      .then((event) => {
        dispatcher({
          type: SportsActions.SPORT_EVENTS_UPDATE,
          payload: {
            [payload.eventId]: event
          }
        })
        dispatcher({
          type: SportsActions.REMOVE_LOADER
        })
      })
      .catch((error) => {
        logger.error('[sport state]', 'unable to load sport events by market type')
        dispatcher({
          type: SportsActions.REMOVE_LOADER,
          payload: {
            error
          }
        })
      })
  }

  /*
  * Sport initialise
  */
  React.useEffect(() => {
    const ac = new AbortController()

    const init = async () => {
      dispatcher({
        type: SportsActions.ADD_LOADER
      })
      // Resolving sport list
      await SportManager.findSports({
        filter: {
          sportServices: [
            // SPORT_SERVICE.LIVE,
            SPORT_SERVICE.PREMATCH
          ],
          isOnlyMainMarketTypes: true,
          isOnlyActiveMarkets: true,
          fromDate: Date.now()
        },
        paging: {
          itemsPerPage: 500,
          currentPage: 0
        }
      }, ac.signal)
        .then(data => {
          dispatcher({
            type: SportsActions.SPORT_LIST_UPDATE,
            payload: data.data
          })
          dispatcher({
            type: SportsActions.REMOVE_LOADER,
            payload: {
              error: null
            }
          })
        })
        .catch(error => {
          logger.error('[sport state]', 'unable to load sport list')
          dispatcher({
            type: SportsActions.REMOVE_LOADER,
            payload: {
              error
            }
          })
        }).finally(() => {
          dispatcher({
            type: SportsActions.SPORT_STATE_UPDATE,
            payload: {
              initialized: true
            }
          })
          sportLoadCategoriesHandler()
          sportLoadTournamentsHandler()
          sportLoadEventsHandler({
            paging: {
              itemsPerPage: 50,
              currentPage: 0
            }
          })
        })
    }

    if (!state.initialized) {
      init()
    }

    // Subscribing to counters
    const subscriptionKeys = [
      {
        notificationTopic: SPORT_EVENT_COUNT,
        id: -1
      },
      {
        notificationTopic: CATEGORY_EVENT_COUNT,
        id: -1
      },
      {
        notificationTopic: TOURNAMENT_EVENT_COUNT,
        id: -1
      }
    ]
    Bus.emit(Bus.events.socket.subscribe, {
      subscriptionKeys: subscriptionKeys
    })

    // Websocket handlers
    Bus.on(Bus.events.sport.sportEventUpdate, eventUpdateHandler)
    Bus.on(Bus.events.sport.sportEventsCount, sportCounterHandler)
    Bus.on(Bus.events.sport.categoryEventsCount, categoryCounterHandler)
    Bus.on(Bus.events.sport.tournamentEventsCount, tournamentCounterHandler)

    Bus.on(Bus.events.sport.sportReInit, sportReInitHandler)
    Bus.on(Bus.events.sport.sportLoadCategories, sportLoadCategoriesHandler)
    Bus.on(Bus.events.sport.sportLoadTournaments, sportLoadTournamentsHandler)
    Bus.on(Bus.events.sport.sportLoadEvents, sportLoadEventsHandler)
    Bus.on(Bus.events.sport.sportLoadEventMarketGroup, sportLoadEventsMarketGroupHandler)
    Bus.on(Bus.events.sport.sportLoadSingleEvent, sportLoadSingleEventHandler)

    return () => {
      Bus.emit(Bus.events.socket.unsubscribe, {
        subscriptionKeys: subscriptionKeys
      })

      // Websocket handlers
      Bus.off(Bus.events.sport.sportEventUpdate, eventUpdateHandler)
      Bus.off(Bus.events.sport.sportEventsCount, sportCounterHandler)
      Bus.off(Bus.events.sport.categoryEventsCount, categoryCounterHandler)
      Bus.off(Bus.events.sport.tournamentEventsCount, tournamentCounterHandler)

      Bus.off(Bus.events.sport.sportReInit, sportReInitHandler)
      Bus.off(Bus.events.sport.sportLoadEvents, sportLoadEventsHandler)
      Bus.off(Bus.events.sport.sportLoadEventMarketGroup, sportLoadEventsMarketGroupHandler)
      Bus.off(Bus.events.sport.sportLoadSingleEvent, sportLoadSingleEventHandler)
      Bus.off(Bus.events.sport.sportLoadTournaments, sportLoadTournamentsHandler)
      Bus.off(Bus.events.sport.sportLoadCategories, sportLoadCategoriesHandler)

      ac.abort()
    }
  },
  [connection.id, lang, state.initialized])

  return [state, dispatcher]
}

const SportContext = React.createContext()

export function SportProvider (props) {
  const {
    value,
    children
  } = props

  const [state, dispatcher] = createSportState(value)

  return (
    <SportContext.Provider value={useMemo(
      () => [state, dispatcher],
      [state, dispatcher])}>
      {children}
    </SportContext.Provider>
  )
}

export const useSportState = () => {
  return React.useContext(SportContext)
}

const sortGeneric = (a, b) => {
  if (a.position > b.position) {
    return 1
  }
  if (a.position < b.position) {
    return -1
  }

  if (a.name > b.name) {
    return 1
  }
  if (a.name < b.name) {
    return -1
  }

  return 0
}

const sortEvents = (a, b) => {
  if (a.startTime > b.startTime) {
    return 1
  }
  if (a.startTime < b.startTime) {
    return -1
  }

  if (a.name > b.name) {
    return 1
  }
  if (a.name < b.name) {
    return -1
  }

  return 0
}
const selectSportById = (id) => {
  const [state] = useSportState()
  return useMemo(() => {
    return state.sportsById[id]
  }, [state.sportsById, id])
}

const selectSports = (ids = null) => {
  const [state] = useSportState()
  return useMemo(() => {
    if (isArray(ids) && !isEmpty(ids)) {
      return ids.reduce((list, id) => {
        if (!state.sportsById[id]) {
          return list
        }
        return [
          ...list,
          state.sportsById[id]
        ]
      }, [])
    }
    return Object.values(state.sportsById).sort(sortGeneric)
  }
  , [state.sportsById, ids])
}

const selectCategoryById = (id) => {
  const [state] = useSportState()
  return useMemo(() => {
    return state.categoriesById[id]
  }, [state.categoriesById, id])
}

const selectCategories = ({ sportId = null, ids = null }) => {
  const [state] = useSportState()
  return useMemo(() => {
    if (isArray(ids) && !isEmpty(ids)) {
      return ids.reduce((list, id) => {
        if (!state.categoriesById[id]) {
          return list
        }
        return [
          ...list,
          state.categoriesById[id]
        ]
      }, [])
    }
    if (sportId) {
      (state.categoriesBySportId[sportId] || []).sort(sortGeneric)
    }
    return Object.values(state.categoriesById).sort(sortGeneric)
  }
  , [state.categoriesById, sportId])
}

const selectCategoriesBySportId = (sportId) => {
  const [state] = useSportState()
  return useMemo(() => (state.categoriesBySportId[sportId] || []).sort(sortGeneric)
    , [state.categoriesBySportId, sportId])
}

const selectTournamentById = (id) => {
  const [state] = useSportState()
  return useMemo(() => {
    return state.tournamentsById[id]
  }, [state.tournamentsById, id])
}

const selectTournaments = ({ sportId = null, categoryId = null, ids = null }) => {
  const [state] = useSportState()
  return useMemo(() => {
    if (isArray(ids) && !isEmpty(ids)) {
      return ids.reduce((list, id) => {
        if (!state.tournamentsById[id]) {
          return list
        }
        return [
          ...list,
          state.tournamentsById[id]
        ]
      }, [])
    }
    if (categoryId) {
      return (state.tournamentsByCategoryId[categoryId] || []).sort(sortEvents)
    }
    if (sportId) {
      return (state.tournamentsBySportId[sportId] || []).sort(sortEvents)
    }
    return Object.values(state.tournamentsById).sort(sortGeneric)
  }
  , [state.tournamentsById, sportId, categoryId])
}

const selectTournamentsBySportId = (sportId) => {
  const [state] = useSportState()
  return useMemo(() => (state.tournamentsBySportId[sportId] || []).sort(sortGeneric)
    , [state.tournamentsBySportId, sportId])
}

const selectTournamentsByCategoryId = (categoryId) => {
  const [state] = useSportState()
  return useMemo(() => (state.tournamentsByCategoryId[categoryId] || []).sort(sortGeneric)
    , [state.tournamentsByCategoryId, categoryId])
}

const selectTopTournaments = ({
  limit = 5,
  excludeOutright = false
}) => {
  const [state] = useSportState()
  return useMemo(() => Object.values(state.tournamentsById)
    .filter(t => t.isTop === true)
    .sort(sortGeneric)
    .slice(0, limit || 5)
    .map(t => {
      return {
        ...t,
        events: (state.eventsByTournamentId[t.id] || [])
          .filter(t => !excludeOutright || !t.outright)
          .sort(sortEvents)
      }
    }), [state.tournamentsById, limit, excludeOutright])
}

const selectTopEvents = ({
  limit = 5,
  excludeOutright = false
}) => {
  const [state] = useSportState()
  return useMemo(() => Object.values(state.eventsById)
    .filter(t => t.isTop === true)
    .filter(t => !excludeOutright || !t.outright)
    .sort(sortEvents)
    .slice(0, limit || 5)
  , [state.eventsById, limit, excludeOutright])
}

const selectUpcomingEvents = ({
  limit = 5,
  excludeOutright = false
}) => {
  const [state] = useSportState()
  return useMemo(() =>
    Object.values(state.eventsById)
      .filter(t => !excludeOutright || !t.outright)
      .sort(sortEvents)
      .slice(0, limit || 5)
  , [state.eventsById, limit, excludeOutright])
}

const selectEventById = (id) => {
  const [state] = useSportState()
  return useMemo(() => {
    return state.eventsById[id]
  }, [state.eventsById, id])
}

const selectCurrentEvent = () => {
  const [state] = useSportState()
  return state.eventsById[state.selectedEventId]
}

const selectIsLoading = () => {
  const [state] = useSportState()
  return !!state.loading
}

const selectEvents = ({ sportId = null, categoryId = null, tournamentId = null, ids = null }) => {
  const [state] = useSportState()
  return useMemo(() => {
    if (isArray(ids) && !isEmpty(ids)) {
      return ids.reduce((list, id) => {
        if (!state.eventsById[id]) {
          return list
        }
        return [
          ...list,
          state.eventsById[id]
        ]
      }, [])
    }
    if (tournamentId) {
      return (state.eventsByTournamentId[tournamentId] || []).sort(sortEvents)
    }
    if (categoryId) {
      return (state.eventsByCategoryId[categoryId] || []).sort(sortEvents)
    }
    if (sportId) {
      return (state.eventsBySportId[sportId] || []).sort(sortEvents)
    }
    return (Object.values(state.eventsById) || []).sort(sortEvents)
  }, [state.eventsById, sportId, categoryId, tournamentId])
}

const selectEventsBySportId = (sportId) => {
  const [state] = useSportState()
  return useMemo(() => (state.eventsBySportId[sportId] || []).sort(sortEvents)
    , [state.eventsBySportId, sportId])
}

const selectEventsByCategoryId = (categoryId) => {
  const [state] = useSportState()
  return useMemo(() => (state.eventsByCategoryId[categoryId] || []).sort(sortEvents)
    , [state.eventsByCategoryId, categoryId])
}

const selectEventsByTournamentId = (tournamentId) => {
  const [state] = useSportState()
  return useMemo(() => (state.eventsByTournamentId[tournamentId] || []).sort(sortEvents)
    , [state.eventsByTournamentId, tournamentId])
}

export const SportSelector = {
  selectSportById,
  selectSports,
  selectCategoryById,
  selectCategories,
  selectCategoriesBySportId,
  selectTournamentById,
  selectTournaments,
  selectTournamentsBySportId,
  selectTournamentsByCategoryId,
  selectTopTournaments,
  selectTopEvents,
  selectUpcomingEvents,
  selectEventById,
  selectEvents,
  selectEventsBySportId,
  selectEventsByCategoryId,
  selectEventsByTournamentId,
  selectCurrentEvent,
  selectIsLoading
}
